/**
 * Anvil screen (vanilla AnvilScreen, 176x166): left (27,47), right (76,47),
 * result (134,47); "Enchantment Cost: N" in green (red / "Too Expensive!" when
 * unaffordable). Combine + repair + rename (keyboard, or a hidden <input> for touch keyboards).
 */

import { CLICK, anvilMenu, type AnvilMenu } from '../../game/menu';
import { isEmpty } from '../../game/types';
import { GuiStage, Label, sheet, sprite, textWidth } from '../kit';
import { ContainerScreen } from './container';
import type { ScreenDeps } from './screenHost';

export interface AnvilCtx {
  level: () => number;
  spendLevels: (n: number) => void;
  /** Called after each use (vanilla: 12% chance the anvil degrades). */
  onUse?: () => void;
}

export class AnvilScreen extends ContainerScreen {
  private readonly anvil: AnvilMenu;
  private readonly costLabel: Label;
  private readonly field: HTMLDivElement;
  private readonly nameLabel: Label;
  /** Text field contents (kept by the menu); undefined until an item is placed. */
  private get text(): string | undefined { return this.anvil.text; }
  private blink = 0;
  private readonly input: HTMLInputElement;

  constructor(deps: ScreenDeps, stage: GuiStage, ctx: AnvilCtx) {
    super(deps, stage);
    this.root.appendChild(sheet('gui/container/anvil.png', 0, 0, 176, 166, 0, 0));
    // 0 left, 1 right, 2 result, 3..29 main, 30..38 hotbar
    this.anvil = this.use(anvilMenu(this.env({ level: ctx.level, spendLevels: ctx.spendLevels }), () => ctx.onUse?.()));
    this.at(0, 27, 47); this.at(1, 76, 47); this.at(2, 134, 47);
    this.placePlayer(3, 84, 142);
    this.addLabel('Repair & Name', 60, 6);
    this.addLabel('Inventory', 8, 72);
    this.costLabel = new Label('', 168, 69, { color: '#80ff20', shadow: true, align: 'right' });
    this.root.appendChild(this.costLabel.el);
    this.field = sprite('gui/sprites/container/anvil/text_field_disabled.png', 59, 20, 110, 16);
    this.nameLabel = new Label('', 62, 24, { color: '#ffffff', shadow: true });
    this.root.append(this.field, this.nameLabel.el);
    // touch / on-screen keyboard: a transparent real <input> over the field takes focus on tap
    this.input = document.createElement('input');
    this.input.type = 'text';
    this.input.maxLength = 50;
    this.input.autocomplete = 'off';
    this.input.spellcheck = false;
    this.input.style.cssText = 'position:absolute;left:59px;top:20px;width:110px;height:16px;opacity:0;border:0;padding:0;font-size:16px;';
    this.input.addEventListener('input', () => {
      if (this.text === undefined) { this.input.value = ''; return; }
      let v = this.input.value.slice(0, 50);
      while (v.length > 0 && textWidth(v) >= 100) v = v.slice(0, -1);
      this.input.value = v;
      this.setText(v);
    });
    this.field.style.pointerEvents = 'auto';
    this.field.addEventListener('pointerdown', (ev) => {
      if (this.text === undefined) return;
      ev.preventDefault();
      this.input.value = this.text;
      this.input.focus();
    });
    this.root.appendChild(this.input);
    this.mount();
    this.anvil.onResult = () => { this.paint(); };
  }

  /** The name field changed: recompute, and tell the server (server mode). */
  private setText(t: string): void {
    this.anvil.text = t;
    this.anvil.recompute();
    this.report(null, 0, CLICK.BUTTON, { text: t });
  }

  /** Repaint the name field and the cost line after the menu recomputed. */
  private paint(): void {
    const m = this.anvil;
    if (m.text === undefined) this.input.value = '';
    this.field.style.backgroundImage = `url(mc/gui/sprites/container/anvil/text_field${isEmpty(m.left) ? '_disabled' : ''}.png)`;
    this.paintName();
    const r = m.res;
    if (!r) this.costLabel.set('');
    else if (r.tooExpensive) { this.costLabel.set('Too Expensive!'); this.costLabel.setColor('#ff6060'); }
    else { this.costLabel.set(`Enchantment Cost: ${r.cost}`); this.costLabel.setColor(m.affordable() ? '#80ff20' : '#ff6060'); }
  }

  private paintName(): void {
    const t = this.text ?? '';
    this.nameLabel.set(t + (this.text !== undefined && this.blink % 40 < 20 ? '_' : ''));
  }

  /** Typing into the name field (vanilla max 50 chars). E must not close while typing. */
  override keyDown(e: KeyboardEvent): boolean {
    if (e.target === this.input) return e.code !== 'Escape'; // the <input> owns typing (handled by its input event)
    if (this.text === undefined || e.ctrlKey || e.metaKey || e.altKey) return false;
    if (e.key === 'Backspace') this.setText(this.text.slice(0, -1));
    else if (e.key.length === 1 && this.text.length < 50 && textWidth(this.text + e.key) < 100) this.setText(this.text + e.key);
    else return false;
    return true;
  }

  override update(): void {
    this.blink++;
    if (this.blink % 20 === 0) this.paintName();
    this.anvil.settle!(); // the player's level can change while open (XP orbs)
  }
}
